import { Flex, Heading, Select, Text } from "@chakra-ui/react";
import axios from "axios";
import { useTranslations } from "contexts/LocalizationContext";
import type { ChangeEvent } from "react";
import { useMemo, useState } from "react";
import type { AirlineScore, AirportScore, Score } from "services/scores";
import { isAirlineScore } from "../../utils";
import ScoreTag from "../ScoreTag";
import type { BestRatedProps } from "../SelectDate/types";
import Card from "./Card";
import { DEFAULT_SELECTED_COUNTRY, countries } from "./consts";
import { localizeAirportName } from "../../AirportRanking/utils";

export const BestByCountry = ({ bestByCountry, scoresType }: Pick<BestRatedProps, "bestByCountry" | "scoresType">) => {
  const [bestScore, setBestScore] = useState(bestByCountry);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SELECTED_COUNTRY);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const translations = useTranslations();

  const onCountryChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
    setCountriesLoading(true);

    try {
      const { data } = await axios.get<AirportScore[] | AirlineScore[]>(
        `/api/scores/get-${scoresType}-by-country/?country=${e.target.value}`
      );

      setBestScore(data);
    } catch (error) {
      setBestScore(bestByCountry);
      setSelectedCountry(DEFAULT_SELECTED_COUNTRY);
      throw Error(error.message);
    } finally {
      setCountriesLoading(false);
    }
  };

  const sortedCountries = useMemo(
    () => countries[scoresType].sort((a, b) => a.name.localeCompare(b.name)),
    [scoresType]
  );

  return (
    <Card>
      <Heading variant="h3" as="h3" mb={2}>
        {translations(`best_${scoresType}_by_country`)}
      </Heading>
      {scoresType === "airlines" && <Text fontSize="md">{translations(`out_of_the_${scoresType}`)}</Text>}
      <Select
        mt={6}
        h="48px"
        maxWidth={{ base: "100%", md: "55%" }}
        mb={4}
        value={selectedCountry}
        onChange={onCountryChange}
        disabled={countriesLoading}
      >
        {sortedCountries.map(({ code }) => (
          <option key={code} value={code}>
            {translations(`country_${code.toLowerCase()}`)}
          </option>
        ))}
      </Select>
      {bestScore.map((item: Score, index: number) => {
        let name: string;
        let score: number;
        let code: string;

        if (isAirlineScore(item)) {
          const { airline, score: airlineScore } = item;
          name = airline;
          score = airlineScore;
        }

        if (!isAirlineScore(item)) {
          const { dep_airport_name: airport, airport_score: airportScore, dep_airport_code: airportCode } = item;
          name = airport;
          score = airportScore;
          code = airportCode;
        }

        return (
          <Flex
            key={name}
            justifyContent="space-between"
            alignItems={{ base: "flex-start", md: "center" }}
            fontSize="sm"
            borderBottom="1px"
            borderColor="greyscale.300"
            py={4}
            position="relative"
            _last={{ borderBottom: "none" }}
            _after={{
              content: `""`,
              position: "absolute",
              bottom: -1,
              height: 2,
              width: 6,
              bgColor: "white",
            }}
          >
            <Flex>
              <Text fontSize="inherit" mr={4}>
                {index + 1}.
              </Text>
              {name && (
                <Text maxW={{ base: "90%", md: "unset" }} fontSize="inherit">
                  {(code && localizeAirportName(code, name, translations)) || name}
                </Text>
              )}
            </Flex>
            <ScoreTag index={index} score={score} />
          </Flex>
        );
      })}
    </Card>
  );
};
